import { useEffect, useState } from "react";
import api from "@/lib/api";
import SetCard from "@/components/SetCard";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";
import { CATEGORIES, getSubcats } from "@/lib/categories";

export default function Discover() {
  const [sets, setSets] = useState([]);
  const [cat, setCat] = useState("all");
  const [sub, setSub] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const subOptions = cat !== "all" ? getSubcats(cat) : [];
  useEffect(() => { setSub("all"); }, [cat]);

  const load = async (retry = 0) => {
    setLoading(true);
    const params = new URLSearchParams();
    if (cat !== "all") params.set("category", cat);
    if (sub !== "all") params.set("subcategory", sub);
    if (q) params.set("q", q);
    try {
      const { data } = await api.get(`/sets?${params.toString()}`);
      setSets(data || []);
      setLoading(false);
    } catch (e) {
      if (retry < 2) {
        setTimeout(() => load(retry + 1), 600 * (retry + 1));
      } else {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [cat, sub, q]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-12">
      <div className="border-b border-[#E5E5E5] pb-8 mb-10">
        <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#9CA47E] mb-3">§ Discover</div>
        <h1 className="font-heading text-5xl sm:text-6xl tracking-tight">Sets from the community</h1>
        <p className="font-body text-[#595959] mt-3 max-w-xl">Browse curated moods. Click any set to view, like or remix.</p>

        <div className="mt-8 flex flex-col gap-3">
          <div className="flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
            <div className="flex gap-2 flex-wrap items-center">
              <button
                type="button"
                data-testid="filter-all"
                onClick={() => setCat("all")}
                className={`px-4 py-2 font-mono text-[10px] uppercase tracking-widest border cursor-pointer transition-colors ${cat === "all" ? "bg-[#333333] text-white border-[#333333]" : "bg-white text-[#333333] border-[#E5E5E5] hover:border-[#333333]"}`}
              >
                All
              </button>
              {CATEGORIES.map((c) => (
                <button
                  key={c.value}
                  type="button"
                  data-testid={`filter-${c.value}`}
                  onClick={() => setCat(c.value)}
                  className={`px-4 py-2 font-mono text-[10px] uppercase tracking-widest border cursor-pointer transition-colors ${cat === c.value ? "bg-[#333333] text-white border-[#333333]" : "bg-white text-[#333333] border-[#E5E5E5] hover:border-[#333333]"}`}
                >
                  {c.label}
                </button>
              ))}
            </div>
            <div className="relative max-w-sm w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#595959]" />
              <Input
                data-testid="search-input"
                placeholder="Search sets, tags, descriptions"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="rounded-none border-[#E5E5E5] focus-visible:ring-0 focus-visible:border-[#333333] pl-10 py-5"
              />
            </div>
          </div>
          {/* Sub-category chip row — only when a top is chosen */}
          {subOptions.length > 0 && (
            <div className="flex gap-1.5 overflow-x-auto pb-1" data-testid="discover-subcat-row">
              <button
                type="button"
                data-testid="discover-sub-all"
                onClick={() => setSub("all")}
                className={`shrink-0 px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest border cursor-pointer transition-colors ${sub === "all" ? "bg-[#333333] text-white border-[#333333]" : "bg-white border-[#E5E5E5] text-[#595959] hover:border-[#333333] hover:text-[#333333]"}`}
              >
                All
              </button>
              {subOptions.map((s) => (
                <button
                  key={s.value}
                  type="button"
                  data-testid={`discover-sub-${s.value}`}
                  onClick={() => setSub(s.value)}
                  className={`shrink-0 px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest border cursor-pointer transition-colors ${sub === s.value ? "bg-[#9CA47E] text-white border-[#9CA47E]" : "bg-white border-[#E5E5E5] text-[#595959] hover:border-[#9CA47E] hover:text-[#9CA47E]"}`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {loading ? (
        <div className="font-mono text-xs uppercase tracking-widest text-[#595959]">Loading…</div>
      ) : sets.length === 0 ? (
        <div className="font-body text-[#595959] py-20 text-center" data-testid="discover-empty">No sets found{sub !== "all" ? ` for ${sub}` : ""}. Be the first to create one.</div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {sets.map((s, i) => (
            <SetCard key={s.id} set={s} hidePalette span={i % 7 === 0 ? "md:col-span-2 md:row-span-2" : ""} />
          ))}
        </div>
      )}
    </div>
  );
}
